import { useState, useEffect, useMemo } from 'react';
import { useWeb3ModalAccount } from '@web3modal/ethers5/react';
import { ContractService } from '../services/ContractService';
import { NetworkManager } from '../services/NetworkManager';
import { TX_STATES, isTransactionInProgress } from '../constants/transactionStates';
import { SafeMath } from '../utils/SafeMath';
import { showToast } from '../utils/toast';
import { INITIAL_STATE } from '../constants/concepts';
import { validateMintAmount, validateChainSelection, validateConceptSelection } from '../utils/validation';
import { useTransactionStates } from './useTransactionStates';

export const useContractState = () => {
    const { address, isConnected } = useWeb3ModalAccount();
    const contractService = useMemo(() => new ContractService(), []);
    const {
        txStates,
        updateTxState,
        resetTxState,
        getButtonState
    } = useTransactionStates();

    const [state, setState] = useState({
        ...INITIAL_STATE,
        address,
        isConnected
    });
    const [showTerms, setShowTerms] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(
        localStorage.getItem('termsAccepted') === 'true'
    );

    // Update state function
    function updateState(updates) {
        setState(prev => ({ ...prev, ...updates }));
    }

    // Load contract data function
    async function loadContractData() {
        try {
            const contractState = await contractService.getContractState();
            const votingData = await contractService.getVotingData();

            // Update state in one go
            updateState({
                price: contractState.price,
                maxMint: contractState.maxLimitPerWallet,
                currentSupply: contractState.totalMinted,
                maxSupply: contractState.supplyLimit,
                whitelistMint: contractState.whitelistMint,
                publicMint: contractState.publicMint,
                isWhitelisted: contractState.isWhitelisted,
                votesConcept: votingData.concepts,
                chainConcept: votingData.chains
            });
        } catch (error) {
            console.error('Data loading error:', error);
            showToast.error(error);
        }
    }

    // Handle mint function
    async function executeMint() {
        if (isTransactionInProgress(txStates)) return;

        try {
            if (!isConnected) {
                throw new Error('Please connect your wallet first');
            }

            validateMintAmount(state.mintAmount, state.maxMint);
            validateConceptSelection(state.selectedConcept);
            validateChainSelection(state.selectedChainNo);

            const value = SafeMath.multiply(
                SafeMath.parseEther(state.price),
                state.mintAmount
            );

            updateTxState('mint', TX_STATES.ESTIMATING);
            const gasConfig = await contractService.estimateMintGas(
                state.selectedConcept,
                state.mintAmount,
                state.selectedChainNo,
                value
            );

            updateTxState('mint', TX_STATES.PENDING);
            const tx = await contractService.mint(
                state.selectedConcept,
                state.mintAmount,
                state.selectedChainNo,
                value,
                gasConfig
            );

            await tx.wait();
            updateTxState('mint', TX_STATES.CONFIRMED);
            showToast.success('Mint Confirmed');

            await loadContractData();
            updateState({ isModalOpen: true });

        } catch (error) {
            console.error('Mint error:', error);
            updateTxState('mint', TX_STATES.FAILED);

            if (error.message.includes('rejected')) {
                showToast.info('Transaction cancelled');
            } else {
                showToast.error(error);
            }
        } finally {
            resetTxState('mint');
        }
    }

    // Handle claim function
    async function executeClaim() {
        if (isTransactionInProgress(txStates)) return;

        try {
            if (!isConnected) {
                throw new Error('Please connect your wallet first');
            }

            if (!state.isWhitelisted) {
                throw new Error('You are not whitelisted for claiming');
            }

            validateConceptSelection(state.selectedConcept);
            validateChainSelection(state.selectedChainNo);

            updateTxState('claim', TX_STATES.ESTIMATING);
            const gasConfig = await contractService.estimateClaimGas(
                state.selectedConcept,
                state.selectedChainNo,
                0
            );

            updateTxState('claim', TX_STATES.PENDING);
            const tx = await contractService.whitelistMint(
                state.selectedConcept,
                state.selectedChainNo,
                gasConfig
            );

            await tx.wait();
            updateTxState('claim', TX_STATES.CONFIRMED);
            showToast.success('Claim Confirmed');

            await loadContractData();
            updateState({ isModalOpen: true });

        } catch (error) {
            console.error('Claim error:', error);
            updateTxState('claim', TX_STATES.FAILED);

            if (error.message.includes('rejected')) {
                showToast.info('Transaction cancelled');
            } else {
                showToast.error(error);
            }
        } finally {
            resetTxState('claim');
        }
    }

    function handleDisconnect() {
        setState({
            ...INITIAL_STATE,
            address: '',
            isConnected: false,
            connectionStatus: "Connect",
            currentNetwork: "Not Connected"
        });
        localStorage.removeItem('termsAccepted');
        setTermsAccepted(false);
    }

    function handleMint() {
        if (!termsAccepted) {
            setShowTerms(true);
            return;
        }
        executeMint();
    }

    function handleClaim() {
        if (!termsAccepted) {
            setShowTerms(true);
            return;
        }
        executeClaim();
    }

    function handleTermsAccept() {
        localStorage.setItem('termsAccepted', 'true');
        setTermsAccepted(true);
        setShowTerms(false);
        executeMint();
    }

    // Effect for handling wallet connection changes
    useEffect(() => {
        updateState({ address, isConnected });
    }, [address, isConnected]);

    // Effect for contract initialization and data loading
    useEffect(() => {
        async function initializeContract() {
            try {
                if (isConnected && address) {
                    await contractService.initialize(address);
                    const network = await NetworkManager.getTargetNetwork();
                    updateState({
                        isConnected: true,
                        connectionStatus: "Disconnect",
                        currentNetwork: network.name.charAt(0).toUpperCase() + network.name.slice(1)
                    });
                } else {
                    await contractService.initialize();
                    updateState({
                        isConnected: false,
                        connectionStatus: "Connect",
                        currentNetwork: "Not Connected"
                    });
                }
                await loadContractData();
            } catch (error) {
                console.error('Initialization error:', error);
                showToast.error(error);
                updateState({
                    isConnected: false,
                    connectionStatus: "Connect",
                    currentNetwork: "Not Connected"
                });
            }
        }

        // Set up polling for contract data updates
        initializeContract();
        const pollInterval = setInterval(loadContractData, 6000); // Poll every 6 seconds

        // Cleanup
        return () => clearInterval(pollInterval);
    }, [address, isConnected, contractService]);

    return {
        state,
        updateState,
        handleMint,
        handleClaim,
        getButtonState,
        loadContractData,
        showTerms,
        setShowTerms,
        handleTermsAccept,
        handleDisconnect
    };
};